import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback } from 'react';

import { MetricCard, MetricCardProps } from '../metric-card';

export type MetricPanelProps<T = unknown> = {
  title?: string;
  hint?: string;
  description?: string;
  metrics: MetricCardProps<T>[];
  onClick?: (metricCard: MetricCardProps<T>) => void;
  children?: React.ReactNode;
};

export const MetricPanel = <T,>({
  title,
  hint,
  metrics,
  description,
  onClick,
  children,
}: MetricPanelProps<T>) => {
  const click = useCallback(
    (metricCard: MetricCardProps<T>) => {
      onClick?.(metricCard);
    },
    [onClick],
  );

  return (
    <Paper
      elevation={2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        borderRadius: 1,
        px: 3,
        pt: 3,
        pb: 2,
      }}
    >
      <Stack direction='column'>
        {title || hint ? (
          <Typography variant='subtitle1' fontWeight={600} gutterBottom={!description}>
            {title}
            {hint && (
              <Typography color='gray' component='span' ml={1}>
                {hint}
              </Typography>
            )}
          </Typography>
        ) : null}
        {description && (
          <Typography variant='body2' color='gray' gutterBottom>
            {description}
          </Typography>
        )}
      </Stack>
      <Box
        display='flex'
        gap={2}
        alignItems='stretch'
        pb={1}
        sx={(theme) => ({
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollBehavior: 'smooth',
          scrollSnapType: 'x mandatory',
          '&::-webkit-scrollbar': {
            width: theme.spacing(0.5),
            height: theme.spacing(0.5),
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.text.primary, 0.2),
            borderRadius: theme.spacing(0.25),
          },
        })}
      >
        {metrics.map((metric) => (
          <Box
            flex={1}
            key={`${metric.label}-${metric.value}`}
            sx={{ ...metric.sx, scrollSnapAlign: 'start' }}
          >
            <MetricCard<T>
              {...metric}
              onClick={onClick ? click : metric.onClick}
              sx={{ height: '100%' }}
            />
          </Box>
        ))}
      </Box>
      {children}
    </Paper>
  );
};
